import { Component, EventEmitter, Input, OnInit, Output, OnChanges, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { AddPopupComponent } from '../add-popup/add-popup.component';
import { faXmark, faTrashCan } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';

@Component({
  selector: 'app-organization-table',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, AddPopupComponent, FontAwesomeModule],
  templateUrl: './organization-table.component.html',
  styleUrls: ['./organization-table.component.css']
})
export class OrganizationTableComponent implements OnInit, OnChanges {
  faXmark = faXmark;
  faTrashCan = faTrashCan;
  showAddEditModal = false;
  showDeleteModal = false;
  isEditMode = false;
  showSkeleton = true;

  organizationForm: FormGroup;
  @Input() organizations: any[] = [];
  @Input() loading = false;
  @Output() addOrganization = new EventEmitter<any>();
  @Output() editOrganization = new EventEmitter<{ id: any; data: any }>();
  @Output() deleteOrganization = new EventEmitter<any>();
  @Input() showSkeletonTrigger = false;

  selectedOrganizationId: number | null = null;

  constructor(private fb: FormBuilder) {
    this.organizationForm = this.fb.group({
      org_name: ['', Validators.required],
      org_slogan: ['', Validators.required],
      org_type: ['', Validators.required],
      org_city: ['', Validators.required],
      org_state: ['', Validators.required],
      org_country: ['', Validators.required],
      org_address1: ['', Validators.required],
      org_address2: ['', Validators.required],
      org_postal_code: ['', Validators.required]
    });
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['showSkeletonTrigger'] && changes['showSkeletonTrigger'].currentValue) {
      this.showTableLoader();
    }
  }

  ngOnInit(): void {
    this.showTableLoader();
  }

  skeletonRows: any[] = [];
  skeletonCols: any[] = [];

  showTableLoader(duration = 800) {
    this.showSkeleton = true;
    const DEFAULT_ROWS = 5;
    const rowCount = (this.organizations && this.organizations.length) ? this.organizations.length : DEFAULT_ROWS;
    const columnCount = 4;
    this.skeletonRows = Array(rowCount).fill(0);
    this.skeletonCols = Array(columnCount).fill(0);

    setTimeout(() => {
      this.showSkeleton = false;
    }, duration);
  }

  // Toggle Add/Edit or Delete modals
  toggleModal(type: 'add' | 'delete') {
    if (type === 'add') {
      this.showAddEditModal = !this.showAddEditModal;
      if (!this.showAddEditModal) {
        this.isEditMode = false;
        this.selectedOrganizationId = null;
        this.organizationForm.reset();
      }
    } else if (type === 'delete') {
      this.showDeleteModal = !this.showDeleteModal;
    }
  }

  openEditModal(item: any) {
    this.isEditMode = true;
    this.showAddEditModal = true;
    this.selectedOrganizationId = item.id;

    this.organizationForm.patchValue({
      org_name: item.name,
      org_slogan: item.slogan,
      org_type: item.type,
      org_city: item.city,
      org_state: item.state,
      org_country: item.country,
      org_address1: item.address1,
      org_address2: item.address2,
      org_postal_code: item.postal_code
    });
  }

  openDeleteModal(item: any) {
    this.selectedOrganizationId = item.id;
    this.showDeleteModal = true;
  }

  confirmDelete() {
    if (this.selectedOrganizationId !== null) {
      this.deleteOrganization.emit(this.selectedOrganizationId);
      this.toggleModal('delete');
      this.selectedOrganizationId = null;
    }
  }

  onSave() {
    if (this.organizationForm.invalid) return;

    const formValue = this.organizationForm.value;
    if (this.isEditMode && this.selectedOrganizationId !== null) {
      this.editOrganization.emit({ id: this.selectedOrganizationId, data: formValue });
    } else {
      this.addOrganization.emit(formValue);
    }
    this.toggleModal('add');
  }
}
